import { useState, useCallback } from 'react';
import { Upload, CheckCircle2, XCircle, Wand2 } from 'lucide-react';
import { copyToClipboard } from '@/lib/clipboard';
import { buildFixupPrompt } from '@/lib/cartographer/fixupPrompt';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { parseWorkflowState } from '@/lib/workflowSchema';
import { validateWorkflowDetailed, VALIDATOR_VERSION, type ValidationReport } from '@/lib/workflowValidation';
import { isCompactFormat, expandCompactWorkflow, compactWorkflowState } from '@/lib/compactWorkflow';
import { isLeanExport, fromLeanExport } from '@/lib/leanExport';
import { autoFixImport } from '@/lib/autoFixImport';
import { checkImportLimits, describeViolations } from '@/lib/workflowLimitsCheck';
import type { PlanLimits } from '@/lib/planTiers';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import { isMultiSheetState } from '@/types/workflow';
import { toast } from '@/hooks/use-toast';

export type ImportMode = 'append' | 'replace';

interface ImportJsonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * C4: om `overLimit=true` laddar caller state:n i preview-only mode
   * (inga mutations tillåtna). Propagated via banner ovanför canvas så
   * user kan uppgradera eller ladda en mindre JSON.
   *
   * `mode` styr om importen ska läggas till på befintlig canvas
   * (`'append'`, default) eller ersätta den (`'replace'`, legacy).
   */
  onImport: (
    state: WorkflowState | MultiSheetWorkflowState,
    mode: ImportMode,
    overLimit?: boolean,
  ) => void;
  /** Plan-limits från usePlan() — importen tier-kollas mot dessa. */
  limits?: PlanLimits;
}

/**
 * Konvertera autoFixImport-resultatets fix-lista till en informativ
 * (men icke-blockerande) toast så user vet vilka tysta justeringar
 * som gjordes på AI-utdata. Tomma listor → ingen toast.
 */
function notifyAutoFixes(fixes: string[]) {
  if (fixes.length === 0) return;
  toast({
    title: 'Importen auto-fixades',
    description: fixes.slice(0, 4).join(' · ') + (fixes.length > 4 ? ` · +${fixes.length - 4} fler` : ''),
  });
}

function ValidationReportView({ report }: { report: ValidationReport }) {
  if (!report.jsonSyntaxOk) {
    return (
      <div className="space-y-2 text-sm font-mono bg-destructive/10 p-3 rounded-md max-h-52 overflow-auto">
        <div className="flex items-center gap-2 text-destructive font-semibold">
          <XCircle className="w-4 h-4 shrink-0" />
          JSON Syntax
        </div>
        <p className="text-destructive pl-6">{report.jsonSyntaxError}</p>
      </div>
    );
  }

  return (
    <div
      className="space-y-2 text-sm font-mono bg-muted/50 p-3 rounded-md max-h-52 overflow-auto"
      data-validator-version={VALIDATOR_VERSION}
    >
      <div className="text-[10px] text-muted-foreground/70 mb-1">
        validator: {VALIDATOR_VERSION} · {report.sections.length} sektion(er)
      </div>
      {report.sections.map((section) => (
        <div key={section.label}>
          <div className={`flex items-center gap-2 font-semibold ${section.ok ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
            {section.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
            {section.label} — {section.ok ? 'OK' : `${section.errors.length} fel`}
          </div>
          {section.errors.length > 0 && (
            <ul className="pl-6 mt-1 space-y-0.5 text-destructive text-xs">
              {section.errors.slice(0, 8).map((err, i) => (
                <li key={i}>• {err}</li>
              ))}
              {section.errors.length > 8 && (
                <li className="text-muted-foreground">… och {section.errors.length - 8} fel till</li>
              )}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * C4: kör tier-limit-check och visar toast om workflow:en överskrider.
 * Return:ar true om overLimit → caller sätter canvas i preview-mode.
 */
function runLimitCheckAndToast(
  state: WorkflowState | MultiSheetWorkflowState,
  limits: PlanLimits,
): boolean {
  const report = checkImportLimits(state, limits);
  if (report.withinLimits) return false;
  toast({
    title: 'Workflow överstiger plan-gräns',
    description: `${describeViolations(report.violations)}. Laddad i preview-mode — uppgradera för att redigera.`,
    variant: 'destructive',
  });
  return true;
}

export function ImportJsonDialog({ open, onOpenChange, onImport, limits }: ImportJsonDialogProps) {
  const [jsonText, setJsonText] = useState('');
  const [report, setReport] = useState<ValidationReport | null>(null);
  // Default-läge är `append` så user kan bygga ett flöde i segment.
  // `replace` är kvar för "starta om från denna JSON"-fallet.
  const [mode, setMode] = useState<ImportMode>('append');

  const handleClose = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setJsonText('');
      setReport(null);
      setMode('append');
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  const handleImport = useCallback(() => {
    // Bound memory + parse time. A 10-block workflow is ≈ 2–4 KB; 5 MB is
    // ~1000× that. Anything larger is almost certainly a mistake (whole-log
    // paste, clipboard chain) and would stall the main thread during
    // JSON.parse. Surface the limit as a validation error so the user can
    // act on it.
    const MAX_IMPORT_BYTES = 5 * 1024 * 1024; // 5 MB
    const byteLength = new Blob([jsonText]).size;
    if (byteLength > MAX_IMPORT_BYTES) {
      setReport({
        jsonSyntaxOk: false,
        jsonSyntaxError: `Filen är för stor (${Math.round(byteLength / 1024 / 1024)} MB). Max ${MAX_IMPORT_BYTES / 1024 / 1024} MB för att undvika att blockera UI:t.`,
        sections: [],
        allOk: false,
      });
      return;
    }
    // Pre-clean: replace JavaScript `undefined` literals with `null` (common AI output mistake)
    const cleaned = jsonText.replace(/:\s*undefined\b/g, ': null');

    const validationReport = validateWorkflowDetailed(cleaned);
    setReport(validationReport);
    if (!validationReport.allOk) return;

    // Wrappa hela parse + onImport-kedjan i try/catch så att en oväntad
    // throw (t.ex. parseWorkflowState returnerar null + non-null assert)
    // INTE lämnar dialogen öppen utan feedback. handleClose körs i
    // finally så dialogen alltid stängs vid lyckad validering.
    try {
      const parsedRaw = JSON.parse(cleaned);


      // Lean-format (Lager A) → hydrate till MultiSheetWorkflowState med
      // tomma defaults för descriptions/icons. handleCopyJson producerar
      // detta format så roundtrip via klippbord blir tappar metadata
      // medvetet (struktur bevaras).
      if (isLeanExport(parsedRaw)) {
        const multi = fromLeanExport(parsedRaw);
        const overLimit = limits ? runLimitCheckAndToast(multi, limits) : false;
        if (overLimit && mode === 'append') {
          // Append-mode kan inte gå via /preview-routen, så vi blockerar
          // hellre än att klippa tyst eller skriva över canvas.
          toast({
            title: 'Importen är för stor för append',
            description: 'Innehållet sprängger din tier-canvas. Byt till "Ersätt canvas" för att granska den i preview-läge.',
            variant: 'destructive',
          });
          return;
        }
        const fixed = autoFixImport(multi);
        notifyAutoFixes(fixed.fixes);
        onImport(fixed.state, mode, overLimit);
        return;
      }

      // Auto-canonicalize till compact-format INNAN expansion. Resultatet
      // av compact:n returneras till caller via onImport som expanded igen
      // (runtime-format).
      let parsed = parsedRaw;
      if (!isCompactFormat(parsed)) {
        const compacted = compactWorkflowState(parsed);
        parsed = expandCompactWorkflow(compacted);
      } else {
        parsed = expandCompactWorkflow(parsed);
      }

      // Multi-sheet path
      if (isMultiSheetState(parsed)) {
        const multi = parsed as MultiSheetWorkflowState;
        const overLimit = limits ? runLimitCheckAndToast(multi, limits) : false;
        if (overLimit && mode === 'append') {
          toast({
            title: 'Importen är för stor för append',
            description: 'Innehållet sprängger din tier-canvas. Byt till "Ersätt canvas" för att granska den i preview-läge.',
            variant: 'destructive',
          });
          return;
        }
        const fixed = autoFixImport(multi);
        notifyAutoFixes(fixed.fixes);
        onImport(fixed.state as MultiSheetWorkflowState, mode, overLimit);
        return;
      }

      const validated = parseWorkflowState(parsed);
      if (!validated) {
        toast({
          title: 'Importen avbröts',
          description: 'Workflow-strukturen kunde inte valideras. Se konsolen för detaljer.',
          variant: 'destructive',
        });
        return;
      }

      const state: WorkflowState = {
        name: validated.name || 'Importerat flöde',
        blocks: validated.blocks.map((b) => ({
          ...b,
          color: b.color || '#6366f1',
        })) as WorkflowState['blocks'],
        connectors: validated.connectors as WorkflowState['connectors'],
        groups: validated.groups as WorkflowState['groups'],
        coordinateOrigin: validated.coordinateOrigin || undefined,
      };

      const overLimit = limits ? runLimitCheckAndToast(state, limits) : false;
      if (overLimit && mode === 'append') {
        toast({
          title: 'Importen är för stor för append',
          description: 'Innehållet sprängger din tier-canvas. Byt till "Ersätt canvas" för att granska den i preview-läge.',
          variant: 'destructive',
        });
        return;
      }
      const fixed = autoFixImport(state);
      notifyAutoFixes(fixed.fixes);
      onImport(fixed.state as WorkflowState, mode, overLimit);
    } catch (err) {
      toast({
        title: 'Importen avbröts',
        description: err instanceof Error ? err.message : 'Okänt fel vid import.',
        variant: 'destructive',
      });
    } finally {
      handleClose(false);
    }
  }, [jsonText, onImport, handleClose, limits, mode]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importera JSON
          </DialogTitle>
          <DialogDescription>
            Klistra in ett WorkflowState JSON-objekt nedan. Det laddas direkt på canvasen.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          className="min-h-[260px] font-mono text-xs"
          placeholder='{"name": "...", "blocks": [...], "connectors": [...], "groups": [...]}'
          value={jsonText}
          onChange={(e) => {
            setJsonText(e.target.value);
            if (report) setReport(null);
          }}
        />

        {report && <ValidationReportView report={report} />}

        {/* Phase 5 of AI/JSON upgrade — let the user copy a structured
            fix-up prompt to their LLM chat instead of debugging by
            hand. Only surfaces when validation found at least one
            actionable error. */}
        {report && !report.jsonSyntaxOk
          ? null
          : report && report.sections.some((s) => !s.ok && s.errors.length > 0) && (
              <div className="flex items-center justify-between gap-2 mt-2 text-xs">
                <span className="text-muted-foreground">
                  Built by an LLM? Copy a structured fix-up prompt and paste it back into the same chat.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={async () => {
                    const prompt = buildFixupPrompt(report, jsonText);
                    try {
                      await copyToClipboard(prompt);
                      toast({
                        title: 'Fix-up prompt kopierat',
                        description: 'Paste in your AI chat and ask for a re-emit.',
                      });
                    } catch {
                      toast({
                        title: 'Kunde inte kopiera',
                        description: 'Open browser permissions and try again.',
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Copy fix-up prompt
                </Button>
              </div>
            )}

        {/* Mode-toggle: append (default) vs replace. Append wrapar
            importen i en ytter-grupp så user kan dra hela batchen. */}
        <div className="flex items-center gap-2 mt-3" role="radiogroup" aria-label="Import mode">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'append'}
            data-testid="import-mode-append"
            onClick={() => setMode('append')}
            className={`flex-1 px-3 py-2 rounded-md border text-sm text-left transition-colors ${
              mode === 'append'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card hover:bg-accent border-border'
            }`}
          >
            <div className="font-medium">Lägg till i canvas</div>
            <div className={`text-[11px] mt-0.5 ${mode === 'append' ? 'text-primary-foreground/85' : 'text-muted-foreground'}`}>
              Importen wrapas i en grupp ned-och-höger om dagens innehåll.
            </div>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'replace'}
            data-testid="import-mode-replace"
            onClick={() => setMode('replace')}
            className={`flex-1 px-3 py-2 rounded-md border text-sm text-left transition-colors ${
              mode === 'replace'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card hover:bg-accent border-border'
            }`}
          >
            <div className="font-medium">Ersätt canvas</div>
            <div className={`text-[11px] mt-0.5 ${mode === 'replace' ? 'text-primary-foreground/85' : 'text-muted-foreground'}`}>
              Skriver över allt nuvarande content. Krävs för stora workflows.
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Avbryt
          </Button>
          <Button onClick={handleImport} disabled={!jsonText.trim()}>
            {mode === 'append' ? 'Lägg till' : 'Ersätt canvas'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
